import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  documentId,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { EMPTY_STATS, type Campaign, type Recipient, type Suppression } from '../../functions/src/shared/types';
import type { ParsedRow } from '../lib/csv';

/** Firestore caps a batch at 500 writes; leave room for the campaign update. */
const BATCH_SIZE = 450;

const campaignsRef = collection(db, 'campaigns');
const suppressionsRef = collection(db, 'suppressions');

export interface NewCampaignInput {
  name: string;
  subject: string;
  html: string;
  text: string;
  ownerUid: string;
  fromEmail: string;
  fromName: string;
  replyTo: string;
  sendsPerMinute: number;
  dailyCap: number;
}

export async function createCampaign(input: NewCampaignInput): Promise<string> {
  const created = await addDoc(campaignsRef, {
    ...input,
    status: 'draft',
    stats: EMPTY_STATS,
    // The Cloud Function re-checks the sending domain before the first send;
    // the client's opinion is never trusted for envelope identity.
    fromIsVerified: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return created.id;
}

export async function updateCampaign(
  campaignId: string,
  patch: Partial<Omit<Campaign, 'id'>>,
): Promise<void> {
  await updateDoc(doc(campaignsRef, campaignId), { ...patch, updatedAt: serverTimestamp() });
}

/**
 * Writes recipients in chunked batches and records the total on the campaign.
 * Suppressed addresses are NOT filtered here — the send loop is the
 * authoritative check, so the list stays accurate if someone unsubscribes
 * between upload and send.
 */
export async function addRecipients(campaignId: string, rows: ParsedRow[]): Promise<void> {
  const recipientsRef = collection(campaignsRef, campaignId, 'recipients');

  for (let start = 0; start < rows.length; start += BATCH_SIZE) {
    const batch = writeBatch(db);
    for (const row of rows.slice(start, start + BATCH_SIZE)) {
      batch.set(doc(recipientsRef), {
        email: row.email,
        mergeData: row.mergeData,
        status: 'queued',
        attempts: 0,
        createdAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }

  await updateCampaign(campaignId, {
    stats: { ...EMPTY_STATS, total: rows.length, queued: rows.length },
  });
}

/** Removes every recipient so a draft's list can be re-uploaded. */
export async function clearRecipients(campaignId: string): Promise<void> {
  const recipientsRef = collection(campaignsRef, campaignId, 'recipients');
  let pending = await getDocs(query(recipientsRef, limit(BATCH_SIZE)));

  while (!pending.empty) {
    const batch = writeBatch(db);
    pending.docs.forEach((snapshot) => batch.delete(snapshot.ref));
    await batch.commit();
    pending = await getDocs(query(recipientsRef, limit(BATCH_SIZE)));
  }

  await updateCampaign(campaignId, { stats: EMPTY_STATS });
}

export async function startCampaign(campaignId: string): Promise<void> {
  // lastError is cleared so an automatic-pause notice doesn't linger once
  // someone has dealt with it and resumed.
  await updateDoc(doc(campaignsRef, campaignId), {
    status: 'sending',
    startedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastError: deleteField(),
  });
}

/**
 * Puts every failed recipient back in the queue with a fresh attempt budget
 * and leaves the campaign paused, so nothing goes out until someone presses
 * Resume. Suppressed, bounced, and complained addresses are untouched.
 *
 * Returns how many recipients were requeued.
 */
export async function retryFailedRecipients(campaignId: string): Promise<number> {
  const recipientsRef = collection(campaignsRef, campaignId, 'recipients');
  const failedQuery = query(recipientsRef, where('status', '==', 'failed'), limit(BATCH_SIZE));
  let requeued = 0;

  let pending = await getDocs(failedQuery);
  while (!pending.empty) {
    const batch = writeBatch(db);
    pending.docs.forEach((snapshot) => {
      batch.update(snapshot.ref, { status: 'queued', attempts: 0, lastError: deleteField() });
    });
    // Counters move in the same batch so they never drift from the rows.
    batch.update(doc(campaignsRef, campaignId), {
      'stats.failed': increment(-pending.size),
      'stats.queued': increment(pending.size),
    });
    await batch.commit();
    requeued += pending.size;
    pending = await getDocs(failedQuery);
  }

  if (requeued > 0) {
    await updateDoc(doc(campaignsRef, campaignId), {
      status: 'paused',
      completedAt: deleteField(),
      updatedAt: serverTimestamp(),
    });
  }
  return requeued;
}

export async function pauseCampaign(campaignId: string): Promise<void> {
  await updateCampaign(campaignId, { status: 'paused' });
}

export async function cancelCampaign(campaignId: string): Promise<void> {
  await updateCampaign(campaignId, { status: 'canceled' });
}

export async function deleteCampaign(campaignId: string): Promise<void> {
  await clearRecipients(campaignId);
  await deleteDoc(doc(campaignsRef, campaignId));
}

export function subscribeCampaigns(
  onChange: (campaigns: Campaign[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(campaignsRef, orderBy('createdAt', 'desc'), limit(100)),
    (snapshot) => {
      onChange(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Campaign));
    },
    onError,
  );
}

export function subscribeCampaign(
  campaignId: string,
  onChange: (campaign: Campaign | null) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(campaignsRef, campaignId),
    (snapshot) => {
      onChange(snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as Campaign) : null);
    },
    onError,
  );
}

/**
 * Live recipient rows. Capped because a campaign detail page has no reason to
 * stream thousands of documents into the browser; the counters on the campaign
 * carry the totals.
 */
export function subscribeRecipients(
  campaignId: string,
  onChange: (recipients: Recipient[]) => void,
  onError: (error: Error) => void,
  max = 500,
): () => void {
  return onSnapshot(
    query(collection(campaignsRef, campaignId, 'recipients'), limit(max)),
    (snapshot) => {
      onChange(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Recipient));
    },
    onError,
  );
}

export function subscribeSuppressions(
  onChange: (suppressions: Suppression[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(suppressionsRef, orderBy('createdAt', 'desc'), limit(500)),
    (snapshot) => {
      onChange(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Suppression));
    },
    onError,
  );
}

export async function addSuppression(email: string, detail?: string): Promise<void> {
  const normalized = email.trim().toLowerCase();
  // Doc ID is the address itself, which makes suppression checks a point read.
  const batch = writeBatch(db);
  batch.set(doc(suppressionsRef, encodeURIComponent(normalized)), {
    email: normalized,
    reason: 'manual',
    detail: detail ?? '',
    createdAt: serverTimestamp(),
  });
  await batch.commit();
}

/** Firestore's limit on values in an `in` filter. */
const IN_QUERY_LIMIT = 30;

export interface BulkSuppressionEntry {
  email: string;
  /** Verification result from an imported file, recorded in the detail. */
  status: string;
}

export interface BulkSuppressionResult {
  added: number;
  alreadySuppressed: number;
}

/**
 * Adds many addresses as manual suppressions. Addresses already on the list
 * are left alone, so an imported file can't overwrite a hard bounce or an
 * unsubscribe with a vaguer reason.
 */
export async function addSuppressions(
  entries: BulkSuppressionEntry[],
  note: string,
): Promise<BulkSuppressionResult> {
  const byId = new Map(
    entries.map((entry) => {
      const email = entry.email.trim().toLowerCase();
      return [encodeURIComponent(email), { email, status: entry.status }] as const;
    }),
  );
  const ids = [...byId.keys()];

  const lookups: Promise<string[]>[] = [];
  for (let start = 0; start < ids.length; start += IN_QUERY_LIMIT) {
    const chunk = ids.slice(start, start + IN_QUERY_LIMIT);
    lookups.push(
      getDocs(query(suppressionsRef, where(documentId(), 'in', chunk))).then((found) =>
        found.docs.map((snapshot) => snapshot.id),
      ),
    );
  }
  const existing = new Set((await Promise.all(lookups)).flat());
  const toAdd = ids.filter((id) => !existing.has(id));

  for (let start = 0; start < toAdd.length; start += BATCH_SIZE) {
    const batch = writeBatch(db);
    for (const id of toAdd.slice(start, start + BATCH_SIZE)) {
      const entry = byId.get(id);
      if (!entry) continue;
      const detail = [note.trim(), entry.status && `status: ${entry.status}`]
        .filter(Boolean)
        .join(' — ');
      batch.set(doc(suppressionsRef, id), {
        email: entry.email,
        reason: 'manual',
        detail,
        createdAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }

  return { added: toAdd.length, alreadySuppressed: existing.size };
}

export async function removeSuppression(suppressionId: string): Promise<void> {
  await deleteDoc(doc(suppressionsRef, suppressionId));
}

export async function isSuppressed(email: string): Promise<boolean> {
  const normalized = email.trim().toLowerCase();
  const found = await getDocs(query(suppressionsRef, where('email', '==', normalized), limit(1)));
  return !found.empty;
}
